import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ViewModuleRoundedIcon from "@mui/icons-material/ViewModuleRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import { useDashboardTheme } from "../../../../context/DashboardThemeContext";
import { useAlert } from "../../../../context/AlertContext";

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getIvaLabel(value) {
  if (Number(value) === 999) return "No objeto";
  return `${Number(value || 0)}%`;
}

function getFinalPrice(product) {
  const pvp = Number(product?.pvp1 || 0);
  const iva = Number(product?.iva_porcentaje || 0);

  if (iva === 999) return pvp;

  return pvp + (pvp * iva) / 100;
}

export default function SearchProduct({
  onSelectProduct,
  showEcommerceCheck = true,
}) {
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();

  const token = localStorage.getItem("accessToken");
  const pageSize = 12;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [ecommerceOnly, setEcommerceOnly] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  const loadCategories = async () => {
    try {
      const params = new URLSearchParams();

      if (ecommerceOnly) {
        params.append("ecommerce_only", "true");
      }

      const response = await fetch(
        `/api/ventas/search-products/categories/?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudieron cargar las categorías");
        return;
      }

      setCategories(data.results || []);
    } catch {
      showError("Error de conexión cargando categorías");
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (debouncedSearch.trim()) {
        params.append("q", debouncedSearch.trim());
      }

      if (ecommerceOnly) {
        params.append("ecommerce_only", "true");
      }

      if (categoryId && categoryId !== "all") {
        params.append("category_id", categoryId);
      }

      params.append("page", String(page));
      params.append("page_size", String(pageSize));
      params.append("limit", String(pageSize));

      const response = await fetch(`/api/ventas/search-products/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudieron cargar los productos");
        setProducts([]);
        setTotalProducts(0);
        return;
      }

      const results = data.results || [];
      const count = Number(data.count || data.total || results.length || 0);

      setProducts(results);
      setTotalProducts(count);
    } catch {
      showError("Error de conexión cargando productos");
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryId, ecommerceOnly]);

  useEffect(() => {
    loadCategories();
  }, [ecommerceOnly]);

  useEffect(() => {
    loadProducts();
  }, [debouncedSearch, categoryId, ecommerceOnly, page]);

  const totalPages = Math.max(1, Math.ceil(totalProducts / pageSize));

  const groupedProducts = useMemo(() => {
    const map = {};

    products.forEach((product) => {
      const categoryName = product.category?.name || "Sin categoría";

      if (!map[categoryName]) {
        map[categoryName] = [];
      }

      map[categoryName].push(product);
    });

    return map;
  }, [products]);

  const nameThreeLinesSx = {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const productCardSx = {
    borderRadius: "14px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    overflow: "hidden",
    boxShadow: "none",
    transition: "all 0.2s ease",
    cursor: "pointer",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      borderColor: dashboardTheme.primary_color,
      transform: "translateY(-2px)",
    },
  };

  const outlinedButtonSx = {
    minHeight: 38,
    px: 2,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const renderProductImage = (product) => (
    <Box
      sx={{
        width: "100%",
        height: 122,
        bgcolor:
          dashboardTheme.theme_mode === "dark"
            ? "rgba(255,255,255,0.04)"
            : "rgba(17,24,39,0.04)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: `1px solid ${dashboardTheme.border_color}`,
        flexShrink: 0,
      }}
    >
      {product.image ? (
        <Box
          component="img"
          src={product.image}
          alt={product.name}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            p: 1,
          }}
        />
      ) : (
        <Inventory2RoundedIcon
          sx={{
            fontSize: 44,
            color: dashboardTheme.muted_text_color,
            opacity: 0.75,
          }}
        />
      )}
    </Box>
  );

  const handleSelectProduct = (product) => {
    if (onSelectProduct) {
      onSelectProduct({
        ...product,
        precio_final: getFinalPrice(product),
      });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: "10px", md: "12px" },
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderBottom: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(260px, 1fr) 230px auto auto",
            },
            gap: 1,
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Buscar producto"
            placeholder="Nombre, código auxiliar o barras"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon
                  sx={{
                    fontSize: 20,
                    color: dashboardTheme.muted_text_color,
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
              ),
            }}
            sx={{
              minWidth: 0,
              ...dashboardTheme.inputStyles,
            }}
          />

          <TextField
            select
            fullWidth
            size="small"
            label="Categoría"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            sx={{
              ...dashboardTheme.inputStyles,
            }}
          >
            <MenuItem value="all">Todas</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>

          {showEcommerceCheck && (
            <Box
              sx={{
                height: 40,
                px: 1.2,
                borderRadius: "12px",
                border: `1px solid ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.card_background,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: "100%", md: "auto" },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ecommerceOnly}
                    onChange={(e) => setEcommerceOnly(e.target.checked)}
                    size="small"
                    sx={{
                      p: 0.35,
                      mr: 0.7,
                      color: dashboardTheme.muted_text_color,
                      "&.Mui-checked": {
                        color: dashboardTheme.primary_color,
                      },
                    }}
                  />
                }
                label="Ecommerce"
                sx={{
                  m: 0,
                  color: dashboardTheme.text_color,
                  whiteSpace: "nowrap",
                  "& .MuiFormControlLabel-label": {
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1,
                  },
                }}
              />
            </Box>
          )}

          <Stack
            direction="row"
            spacing={0.7}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            sx={{
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Tooltip title="Vista tarjetas">
              <IconButton
                onClick={() => setViewMode("grid")}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  color:
                    viewMode === "grid"
                      ? dashboardTheme.primary_color
                      : dashboardTheme.text_color,
                  bgcolor:
                    viewMode === "grid"
                      ? `${dashboardTheme.primary_color}18`
                      : "transparent",
                  "&:hover": {
                    bgcolor: `${dashboardTheme.primary_color}18`,
                  },
                }}
              >
                <ViewModuleRoundedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Vista lista">
              <IconButton
                onClick={() => setViewMode("list")}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  color:
                    viewMode === "list"
                      ? dashboardTheme.primary_color
                      : dashboardTheme.text_color,
                  bgcolor:
                    viewMode === "list"
                      ? `${dashboardTheme.primary_color}18`
                      : "transparent",
                  "&:hover": {
                    bgcolor: `${dashboardTheme.primary_color}18`,
                  },
                }}
              >
                <ViewListRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        {loading && (
          <Typography
            sx={{
              color: dashboardTheme.muted_text_color,
              fontSize: 14,
              py: 3,
              textAlign: "center",
            }}
          >
            Cargando productos...
          </Typography>
        )}

        {!loading && products.length === 0 && (
          <Typography
            sx={{
              color: dashboardTheme.muted_text_color,
              fontSize: 14,
              py: 3,
              textAlign: "center",
            }}
          >
            No hay productos activos para mostrar
          </Typography>
        )}

        {!loading &&
          viewMode === "grid" &&
          Object.entries(groupedProducts).map(([categoryName, items]) => (
            <Box key={categoryName} sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: dashboardTheme.primary_color,
                  mb: 1.2,
                }}
              >
                {categoryName}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {items.map((product) => (
                  <Paper
                    key={product.id}
                    elevation={0}
                    onClick={() => handleSelectProduct(product)}
                    sx={productCardSx}
                  >
                    {renderProductImage(product)}

                    <Box
                      sx={{
                        p: 1.4,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        title={product.name}
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: dashboardTheme.text_color,
                          lineHeight: 1.25,
                          minHeight: 52,
                          ...nameThreeLinesSx,
                        }}
                      >
                        {product.name}
                      </Typography>

                      <Box sx={{ flex: 1 }} />

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 1.3 }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: dashboardTheme.primary_color,
                              lineHeight: 1.2,
                            }}
                          >
                            ${formatMoney(getFinalPrice(product))}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 12,
                              color: dashboardTheme.muted_text_color,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Precio final · IVA {getIvaLabel(product.iva_porcentaje)}
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddRoundedIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSelectProduct(product);
                          }}
                          sx={{
                            ...dashboardTheme.buttonStyles.contained,
                            minHeight: 34,
                            px: 1.4,
                            borderRadius: "10px",
                            flexShrink: 0,
                          }}
                        >
                          Añadir
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          ))}

        {!loading && viewMode === "list" && (
          <Stack spacing={1}>
            {products.map((product) => (
              <Paper
                key={product.id}
                elevation={0}
                onClick={() => handleSelectProduct(product)}
                sx={{
                  borderRadius: "14px",
                  border: `1px solid ${dashboardTheme.border_color}`,
                  bgcolor: dashboardTheme.card_background,
                  p: 1,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "58px minmax(0, 1fr)",
                    sm: "58px minmax(0, 1fr) auto auto",
                  },
                  alignItems: "center",
                  gap: 1.3,
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: dashboardTheme.primary_color,
                    bgcolor: `${dashboardTheme.primary_color}0D`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: "12px",
                    overflow: "hidden",
                    bgcolor:
                      dashboardTheme.theme_mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(17,24,39,0.04)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {product.image ? (
                    <Box
                      component="img"
                      src={product.image}
                      alt={product.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        p: 0.5,
                      }}
                    />
                  ) : (
                    <Inventory2RoundedIcon
                      sx={{
                        color: dashboardTheme.muted_text_color,
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    title={product.name}
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: dashboardTheme.text_color,
                      lineHeight: 1.25,
                      ...nameThreeLinesSx,
                    }}
                  >
                    {product.name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: dashboardTheme.muted_text_color,
                      mt: 0.4,
                    }}
                  >
                    IVA {getIvaLabel(product.iva_porcentaje)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    textAlign: { xs: "left", sm: "right" },
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: dashboardTheme.primary_color,
                    }}
                  >
                    ${formatMoney(getFinalPrice(product))}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: dashboardTheme.muted_text_color,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Precio final
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelectProduct(product);
                  }}
                  sx={{
                    ...outlinedButtonSx,
                    display: { xs: "none", sm: "inline-flex" },
                  }}
                >
                  Añadir
                </Button>
              </Paper>
            ))}
          </Stack>
        )}

        {!loading && totalProducts > pageSize && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              pt: 2,
              mt: 2,
              borderTop: `1px solid ${dashboardTheme.border_color}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: dashboardTheme.muted_text_color,
              }}
            >
              Mostrando {products.length} de {totalProducts} productos
            </Typography>

            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: dashboardTheme.text_color,
                  borderColor: dashboardTheme.border_color,
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  bgcolor: `${dashboardTheme.primary_color}22`,
                  color: dashboardTheme.primary_color,
                  fontWeight: 700,
                },
                "& .MuiPaginationItem-root.Mui-selected:hover": {
                  bgcolor: `${dashboardTheme.primary_color}30`,
                },
              }}
            />
          </Stack>
        )}
      </Box>
    </Paper>
  );
}